// Shared "citations pill → fetch report pieces → dialog" state machine, extracted out of
// `MessageContent.jsx` so the agentic pipeline's `[[cite:id,...]]` markers
// (`../../../agenticRun/components/markdown/AgenticMarkdownComponents.jsx`) can open the exact
// same `CitationsPayloadDialog` without re-implementing this fetch/loading/error lifecycle.
import {useCallback, useEffect, useState} from 'react';
import {fetchCitationReportPieces} from '../../../services/api/citationPayloadApi';

const INITIAL_CITATIONS_DIALOG_STATE = {
    open: false,
    payload: null,
    requestStatus: 'idle',
    responsePayload: null,
    errorMessage: '',
};

/** @returns {{citationsDialogState: object, handleOpenCitations: Function, handleCloseCitations: Function, handleRetryCitations: Function}} */
export function useCitationsDialog() {
    const [citationsDialogState, setCitationsDialogState] = useState(INITIAL_CITATIONS_DIALOG_STATE);

    const handleOpenCitations = useCallback((payload) => {
        setCitationsDialogState({
            open: true,
            payload,
            requestStatus: 'loading',
            responsePayload: null,
            errorMessage: '',
        });
    }, []);

    const handleCloseCitations = useCallback(() => {
        setCitationsDialogState((previousState) => ({
            ...previousState,
            open: false,
        }));
    }, []);

    const handleRetryCitations = useCallback(() => {
        setCitationsDialogState((previousState) => {
            if (!previousState.payload) {
                return previousState;
            }

            return {
                ...previousState,
                requestStatus: 'loading',
                responsePayload: null,
                errorMessage: '',
            };
        });
    }, []);

    useEffect(() => {
        if (!citationsDialogState.open || !citationsDialogState.payload || citationsDialogState.requestStatus !== 'loading') {
            return undefined;
        }

        let cancelled = false;

        fetchCitationReportPieces(citationsDialogState.payload)
            .then((responsePayload) => {
                if (cancelled) {
                    return;
                }

                setCitationsDialogState((previousState) => ({
                    ...previousState,
                    requestStatus: 'success',
                    responsePayload,
                    errorMessage: '',
                }));
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }

                setCitationsDialogState((previousState) => ({
                    ...previousState,
                    requestStatus: 'error',
                    responsePayload: null,
                    errorMessage: error?.message || 'Failed to load citation results.',
                }));
            });

        return () => {
            cancelled = true;
        };
    }, [citationsDialogState.open, citationsDialogState.payload, citationsDialogState.requestStatus]);

    return {citationsDialogState, handleOpenCitations, handleCloseCitations, handleRetryCitations};
}

export default useCitationsDialog;
